/**
 * The shopper's saved vehicle, shared by every part of the shop that cares about
 * fitment: the header chip, the product page verdict, the card badges, and the
 * narrowing on collection and search pages.
 *
 * The app proxy answers "which parts fit this vehicle" with a definitive list, so
 * one call per vehicle is enough and everything else reads from the cached result.
 * Nothing here asks "does part X fit", because the API has no such endpoint.
 *
 * State lives in three places, each for a reason:
 *   localStorage   the vehicle itself, so it survives across visits
 *   sessionStorage the fitting handles, so a reload does not re-hit the proxy, and
 *                  the "show all parts" escape, which should not outlive the visit
 *   the URL        a ?vehicle= parameter, so a narrowed page can be linked and
 *                  survives a reload with storage disabled
 */
(function () {
  'use strict';

  var VEHICLE_KEY = 'aoem:vehicle';
  var SHOW_ALL_KEY = 'aoem:show-all';
  var FITMENT_PREFIX = 'aoem:fitment:';
  var URL_PARAM = 'vehicle';
  var EVENT = 'aoem:vehicle-change';

  var config = readConfig();
  var inFlight = {};

  // ── storage, tolerant of being switched off ───────────────────────────────

  function readStore(store, key) {
    try {
      return window[store].getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      if (value === null) window[store].removeItem(key);
      else window[store].setItem(key, value);
    } catch (e) {
      /* Private browsing, or storage full. The URL parameter still carries state. */
    }
  }

  function readConfig() {
    var node = document.querySelector('[data-aoem-garage-config]');
    var proxyBase = (node && node.dataset.proxyBase) || '/apps/vin-lookup';
    var routeRoot = (node && node.dataset.routeRoot) || '/';

    return {
      proxyBase: proxyBase.replace(/\/$/, ''),
      routeRoot: routeRoot.replace(/\/?$/, '/')
    };
  }

  // ── the vehicle ───────────────────────────────────────────────────────────

  /**
   * A vehicle is either a decoded VIN or a full Year/Make/Model/Submodel/Engine
   * selection. Both reduce to a short key, which is what goes in the URL and what
   * the fitment cache is filed under.
   */
  function keyOf(vehicle) {
    if (!vehicle) return '';
    if (vehicle.kind === 'vin') return 'vin.' + vehicle.vin;

    return [
      'ymme',
      vehicle.year,
      vehicle.makeId,
      vehicle.modelId,
      vehicle.subModelId,
      vehicle.engineBaseId
    ].join('.');
  }

  function parseKey(key) {
    if (!key) return null;
    var parts = String(key).split('.');

    if (parts[0] === 'vin' && parts[1]) return { kind: 'vin', vin: parts[1] };

    if (parts[0] === 'ymme' && parts.length === 6) {
      return {
        kind: 'ymme',
        year: parts[1],
        makeId: parts[2],
        modelId: parts[3],
        subModelId: parts[4],
        engineBaseId: parts[5]
      };
    }
    return null;
  }

  function lookupPath(vehicle) {
    if (vehicle.kind === 'vin') {
      return '/vin-to-products?vin=' + encodeURIComponent(vehicle.vin);
    }
    return (
      '/ymme-to-products?year=' + encodeURIComponent(vehicle.year) +
      '&makeId=' + encodeURIComponent(vehicle.makeId) +
      '&modelId=' + encodeURIComponent(vehicle.modelId) +
      '&subModelId=' + encodeURIComponent(vehicle.subModelId) +
      '&engineBaseId=' + encodeURIComponent(vehicle.engineBaseId)
    );
  }

  function get() {
    // The URL wins over storage, so a shared link shows the vehicle it describes
    // rather than whatever the recipient happened to have saved.
    var fromUrl = parseKey(new URLSearchParams(window.location.search).get(URL_PARAM));
    if (fromUrl) {
      var saved = readSaved();
      // Keep the saved label if it is the same vehicle; the URL carries no label.
      if (saved && keyOf(saved) === keyOf(fromUrl)) return saved;
      return fromUrl;
    }
    return readSaved();
  }

  function readSaved() {
    var raw = readStore('localStorage', VEHICLE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      return parsed && parsed.kind ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function set(vehicle) {
    if (!vehicle || !keyOf(vehicle)) return;

    writeStore('localStorage', VEHICLE_KEY, JSON.stringify(vehicle));
    // A new vehicle means the previous "show all" decision no longer applies.
    writeStore('sessionStorage', SHOW_ALL_KEY, null);
    syncUrl(vehicle);
    announce(vehicle);
  }

  function clear() {
    writeStore('localStorage', VEHICLE_KEY, null);
    writeStore('sessionStorage', SHOW_ALL_KEY, null);
    syncUrl(null);
    announce(null);
  }

  /**
   * Keeps ?vehicle= in step without adding history entries, so Back still means
   * "the previous page" rather than "the previous vehicle".
   */
  function syncUrl(vehicle) {
    if (!window.history || !window.history.replaceState) return;

    var url = new URL(window.location.href);
    if (vehicle) url.searchParams.set(URL_PARAM, keyOf(vehicle));
    else url.searchParams.delete(URL_PARAM);

    window.history.replaceState(window.history.state, '', url.toString());
  }

  function announce(vehicle) {
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { vehicle: vehicle } }));
  }

  function label(vehicle) {
    if (!vehicle) return '';
    if (vehicle.label) return vehicle.label;
    return vehicle.kind === 'vin' ? 'VIN ' + vehicle.vin : 'Your vehicle';
  }

  // ── fitment ───────────────────────────────────────────────────────────────

  /**
   * Reads the storefront handle out of a listing URL. The API records
   * "{storefront}/products/{handle}" normally, but falls back to an admin URL when
   * Shopify returned no handle; that form has no storefront product behind it.
   */
  function handleFromUrl(listingUrl) {
    if (!listingUrl) return null;

    var path;
    try {
      path = new URL(listingUrl).pathname;
    } catch (e) {
      return null;
    }

    var segments = path.split('/').filter(Boolean);
    return segments.length === 2 && segments[0].toLowerCase() === 'products'
      ? decodeURIComponent(segments[1])
      : null;
  }

  /**
   * The handles that fit the saved vehicle, plus the vehicle as the API describes
   * it. Resolves to null when no vehicle is set, and rejects only on a transport
   * or rate-limit failure, so callers can tell "no vehicle" from "lookup failed".
   */
  function fitment() {
    var vehicle = get();
    if (!vehicle) return Promise.resolve(null);

    var key = keyOf(vehicle);
    var cached = readStore('sessionStorage', FITMENT_PREFIX + key);

    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        return Promise.resolve({
          handles: parsed.handles,
          label: parsed.label,
          vehicle: vehicle
        });
      } catch (e) {
        /* fall through and re-fetch */
      }
    }

    // Several components ask on the same page load; they should share one request.
    if (inFlight[key]) return inFlight[key];

    inFlight[key] = fetch(config.proxyBase + lookupPath(vehicle), {
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) {
          var error = new Error('HTTP ' + response.status);
          error.status = response.status;
          throw error;
        }
        return response.json();
      })
      .then(function (json) {
        // The API wraps responses as { message, result }; unwrap if present.
        var data = json && json.result !== undefined ? json.result : json;

        var handles = ((data && data.products) || [])
          .map(function (product) {
            return handleFromUrl(product.shopifyProductUrl);
          })
          .filter(Boolean);

        var described = describe(data && data.vehicle) || label(vehicle);

        writeStore(
          'sessionStorage',
          FITMENT_PREFIX + key,
          JSON.stringify({ handles: handles, label: described })
        );

        // The label only exists after the API has described the vehicle, so this is
        // the first point at which a shared link can show a readable chip.
        if (vehicle.label !== described) {
          vehicle.label = described;
          writeStore('localStorage', VEHICLE_KEY, JSON.stringify(vehicle));
        }

        return { handles: handles, label: described, vehicle: vehicle };
      })
      .finally(function () {
        delete inFlight[key];
      });

    return inFlight[key];
  }

  function describe(vehicle) {
    if (!vehicle) return '';
    return [vehicle.year, vehicle.make, vehicle.model, vehicle.trim, vehicle.engine]
      .filter(Boolean)
      .join(' ');
  }

  // ── the "show all parts" escape ───────────────────────────────────────────

  function isShowingAll() {
    return readStore('sessionStorage', SHOW_ALL_KEY) === '1';
  }

  function showAll() {
    writeStore('sessionStorage', SHOW_ALL_KEY, '1');
    announce(get());
  }

  function narrowAgain() {
    writeStore('sessionStorage', SHOW_ALL_KEY, null);
    announce(get());
  }

  /**
   * The homepage app block publishes its lookups rather than storing them, so the
   * storage shape is defined here only. Adopting the result also seeds the fitment
   * cache, which means choosing a vehicle on the homepage costs no second request
   * when the shopper reaches a product or collection page.
   */
  document.addEventListener('aoem:lookup', function (event) {
    var detail = event.detail || {};
    var vehicle = detail.vehicle;
    var data = detail.data;
    if (!vehicle || !data || !data.vehicle) return;

    var described = describe(data.vehicle) || label(vehicle);
    var handles = ((data.products) || [])
      .map(function (product) {
        return handleFromUrl(product.shopifyProductUrl);
      })
      .filter(Boolean);

    vehicle.label = described;

    writeStore(
      'sessionStorage',
      FITMENT_PREFIX + keyOf(vehicle),
      JSON.stringify({ handles: handles, label: described })
    );
    set(vehicle);
  });

  window.AoemGarage = {
    EVENT: EVENT,
    config: config,
    get: get,
    set: set,
    clear: clear,
    label: label,
    keyOf: keyOf,
    fitment: fitment,
    isShowingAll: isShowingAll,
    showAll: showAll,
    narrowAgain: narrowAgain
  };

  // A vehicle arriving by shared link is not in storage yet. Adopting it here means
  // the rest of the page can treat storage as the single source of truth.
  var fromUrl = parseKey(new URLSearchParams(window.location.search).get(URL_PARAM));
  if (fromUrl) {
    var saved = readSaved();
    if (!saved || keyOf(saved) !== keyOf(fromUrl)) {
      writeStore('localStorage', VEHICLE_KEY, JSON.stringify(fromUrl));
    }
  }

  document.dispatchEvent(new CustomEvent('aoem:garage-ready'));
})();
